/** Shared navigation loading signals for instant click feedback. */

type Listener = (active: boolean) => void;

let active = false;
let startedAt = 0;
let finishTimer: ReturnType<typeof setTimeout> | null = null;
let safetyTimer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<Listener>();

/** Minimum time the branded loader stays visible so fast routes still feel intentional (reduced to 60ms for snappy response). */
const MIN_VISIBLE_MS = 60;

/** Hard cap so a failed/aborted soft navigation never traps the UI. */
const MAX_VISIBLE_MS = 8000;

function clearSafetyTimer() {
  if (safetyTimer) {
    clearTimeout(safetyTimer);
    safetyTimer = null;
  }
}

function notify(next: boolean) {
  listeners.forEach((fn) => fn(next));
}

function forceFinish() {
  clearSafetyTimer();
  if (finishTimer) {
    clearTimeout(finishTimer);
    finishTimer = null;
  }
  if (!active) return;
  active = false;
  notify(false);
}

export function isNavigationLoading(): boolean {
  return active;
}

export function subscribeNavigationLoading(listener: Listener): () => void {
  listeners.add(listener);
  listener(active);
  return () => listeners.delete(listener);
}

export function startNavigationLoading(): void {
  if (finishTimer) {
    clearTimeout(finishTimer);
    finishTimer = null;
  }
  clearSafetyTimer();
  startedAt = Date.now();
  safetyTimer = setTimeout(() => {
    safetyTimer = null;
    forceFinish();
  }, MAX_VISIBLE_MS);

  if (active) return;
  active = true;
  notify(true);
}

/** Start loading when navigating via sidebar or programmatic link — skips same-route clicks. */
export function startNavigationLoadingForHref(href: string, currentPathname?: string | null): void {
  if (typeof window === 'undefined') {
    startNavigationLoading();
    return;
  }

  try {
    const nextPath = (new URL(href, window.location.origin).pathname || '/').replace(/\/$/, '') || '/';
    const currentPath = ((currentPathname ?? window.location.pathname).split('?')[0] || '/').replace(/\/$/, '') || '/';
    if (nextPath === currentPath) return;
  } catch {
    /* invalid href — still show loader */
  }

  startNavigationLoading();
}

export function finishNavigationLoading(): void {
  if (!active) {
    clearSafetyTimer();
    return;
  }

  const elapsed = Date.now() - startedAt;
  const wait = Math.max(0, MIN_VISIBLE_MS - elapsed);

  if (finishTimer) clearTimeout(finishTimer);

  finishTimer = setTimeout(() => {
    finishTimer = null;
    forceFinish();
  }, wait);
}
